import { Taxons_getTaxonMatch } from '@/api/TaxonomyApi/generated/taxons/taxons';
import { Assets_uploadAsset } from '@/api/WhisApi/generated/assets/assets';
import {
  AssetBodyDtoType,
  type AssetBodyDto,
} from '@/api/WhisApi/generated/model';
import { Reports_create } from '@/api/WhisApi/generated/reports/reports';
import type {
  ReportFormData,
  ReportSubmissionMode,
} from '@/screens/ReportCreation/reportCreation.types';
import { buildCreateReportDto } from '@/screens/ReportCreation/reportCreation.utils';
import { buildUploadFile } from '@/screens/ReportCreation/utils/supportingDocumentsUpload.utils';
import type { UploadedDocument } from '@/types/formField.types';

import type { QueuedReportSubmission } from './offlineReportSubmissionQueue.types';

type SubmissionError = {
  statusCode?: number;
  message?: string;
};

const normalizeSubmissionError = (error: unknown): SubmissionError => {
  if (typeof error !== 'object' || error === null) {
    return {};
  }

  const candidate = error as Record<string, unknown>;
  const statusCode =
    typeof candidate.statusCode === 'number' ? candidate.statusCode : undefined;
  const message =
    typeof candidate.message === 'string' ? candidate.message : undefined;

  return { statusCode, message };
};

export const isOfflineSubmissionError = (error: unknown): boolean => {
  const { statusCode, message } = normalizeSubmissionError(error);

  if (statusCode === 0) {
    return true;
  }

  if (!message) {
    return false;
  }

  const loweredMessage = message.toLowerCase();
  return (
    loweredMessage.includes('network') ||
    loweredMessage.includes('internet') ||
    loweredMessage.includes('connection')
  );
};

export const isUnauthorizedSubmissionError = (error: unknown): boolean => {
  const { statusCode } = normalizeSubmissionError(error);
  return statusCode === 401 || statusCode === 403;
};

const uploadDocuments = async (
  documents?: UploadedDocument[],
): Promise<AssetBodyDto[] | undefined> => {
  if (!documents?.length) {
    return undefined;
  }

  const existingAssets = documents
    .filter((document) => Boolean(document.fileName))
    .map((document) => ({
      name: document.name,
      fileName: document.fileName as string,
      type: AssetBodyDtoType.supportingDocument,
    }));

  const newDocuments = documents.filter((document) => !document.fileName);

  const uploadedAssets = await Promise.all(
    newDocuments.map(async (document) => {
      const file = await buildUploadFile(document);
      const assetResponse = await Assets_uploadAsset({ file });

      return {
        name: assetResponse.name ?? document.name,
        fileName: assetResponse.fileName,
        type: AssetBodyDtoType.supportingDocument,
      };
    }),
  );

  const assets = [...existingAssets, ...uploadedAssets];
  return assets.length ? assets : undefined;
};

const getTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const resolveSpeciesForQueuedSubmission = async (
  values: ReportFormData,
): Promise<{ values: ReportFormData; shouldForceDraft: boolean }> => {
  const species = getTrimmedString(values.species);

  if (!species) {
    return { values, shouldForceDraft: true };
  }

  try {
    const matchedTaxon = await Taxons_getTaxonMatch({
      search: species,
    });

    const resolvedSpecies =
      getTrimmedString(matchedTaxon?.scientificName) ??
      getTrimmedString(matchedTaxon?.code);

    if (resolvedSpecies) {
      return {
        values: {
          ...values,
          species: resolvedSpecies,
        },
        shouldForceDraft: false,
      };
    }

    return { values, shouldForceDraft: true };
  } catch {
    return { values, shouldForceDraft: true };
  }
};

const submitWithMode = async (
  queuedSubmission: QueuedReportSubmission,
  mode: ReportSubmissionMode,
  values: ReportFormData = queuedSubmission.values,
): Promise<void> => {
  const assets = await uploadDocuments(values.supportingDocuments);
  const dto = buildCreateReportDto(values, mode, {
    code: queuedSubmission.reportCode ?? queuedSubmission.id,
    offlineMode: true,
    ...(queuedSubmission.versionNote && {
      versionNote: queuedSubmission.versionNote,
    }),
    ...(assets && { assets }),
  });

  await Reports_create(dto);
};

export const processQueuedSubmission = async (
  queuedSubmission: QueuedReportSubmission,
): Promise<void> => {
  const { values: resolvedValues, shouldForceDraft } =
    await resolveSpeciesForQueuedSubmission(queuedSubmission.values);

  if (queuedSubmission.mode === 'draft' || shouldForceDraft) {
    await submitWithMode(queuedSubmission, 'draft', resolvedValues);
    return;
  }

  try {
    await submitWithMode(queuedSubmission, 'published', resolvedValues);
  } catch {
    await submitWithMode(queuedSubmission, 'draft', resolvedValues);
  }
};
